import {
  Button,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderTitle,
  PageHeaderTitleAction,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { isEmptyString } from '@/utils/string';
import type { Collection } from '@/services/db/collection/entity';
import { useCollectionDetailsModal } from './collection-details-modal';
import { useCollectionFormModal } from './collection-form-modal';
import { DeleteCollectionButton } from './delete-collection-button';
import { CollectionImportErrorBanner } from './collection-import-error-banner';

type CollectionDetailHeaderProps = {
  collection: Collection;
  workspaceId: number;
  onDeleted?: () => void;
};

export function CollectionDetailHeader({ collection, workspaceId, onDeleted }: CollectionDetailHeaderProps) {
  const { hasPermission } = usePermissions();
  const detailsModal = useCollectionDetailsModal();
  const reimportModal = useCollectionFormModal();

  const canEdit = hasPermission('journeys/collections/edit') === true;
  const canDelete = hasPermission('journeys/collections/delete') === true;
  const hasDescription = !isEmptyString(collection.description);

  const openDetailsModal = () => detailsModal.open({ collection, workspaceId });
  const openReimportModal = () => reimportModal.open({ workspaceId, collection });

  return (
    <PageHeader>
      <PageHeaderTitle>{collection.name}</PageHeaderTitle>
      {canEdit ? (
        <PageHeaderTitleAction>
          <Button
            variant="subtle"
            icon="edit"
            isIconOnly
            aria-label="Edit collection details"
            size="sm"
            isDisabled={collection.importing}
            onClick={openDetailsModal}
          />
        </PageHeaderTitleAction>
      ) : null}

      <PageHeaderActions>
        <Button variant="secondary" isDisabled={!canEdit || collection.importing} onClick={openReimportModal}>
          Update collection
        </Button>
        <DeleteCollectionButton
          collection={collection}
          workspaceId={workspaceId}
          canDelete={canDelete}
          onDeleted={onDeleted}
        />
      </PageHeaderActions>

      <PageHeaderDescription>{hasDescription ? collection.description : 'No description'}</PageHeaderDescription>

      <CollectionImportErrorBanner
        collection={collection}
        workspaceId={workspaceId}
        canEdit={canEdit}
        onTryAgain={openReimportModal}
      />
    </PageHeader>
  );
}
